<template>
  <Dropdown
    fullwidth
    :label="chosenValue === '' ? 'Choose an option' : chosenValue"
    anchor="right"
    width="20rem"
  >
    <Menu>
      <MenuLabel>Choose an option</MenuLabel>
      <MenuItem
        v-for="option in fieldOptions"
        :key="option.value"
        class="field-option"
        :active="option.value === selectedOption.value"
        @click="selectOption(option)"
      >
        <div>
          <p>{{ option.value }}</p>
          <div class="field-source-info">
            <p>
              Confidence: <ConfidenceLevel :confidence="option.confidence" />
            </p>
            <TruncatedText :text="option.source" />
          </div>
        </div>
      </MenuItem>
      <MenuLabel>Or enter a value manually</MenuLabel>
      <input
        v-model="inputValue"
        class="field-input stk-input"
        type="text"
        @change="handleInputChange"
      >
    </Menu>
  </Dropdown>
</template>

<script setup>
import { defineEmits, ref, watch } from 'vue';
import { ConfidenceLevel, Dropdown, Menu, MenuItem, MenuLabel, TruncatedText } from '@stakeui';

defineProps({
  fieldOptions: {
    type: Array,
    required: true,
  },
  chosen: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['update']);

const chosenValue = ref('');
const chosenSource = ref('');
const chosenConfidence = ref('');
const chosenExplanation = ref('');
const selectedOption = ref({});
const inputValue = ref('');

const selectOption = option => {
  if (option.value === selectedOption.value.value) {
    selectedOption.value = {};
    return;
  }

  selectedOption.value = option;
};

const handleInputChange = () => {
  selectedOption.value = {};
};

const setChosen = (value = '', source = '', confidence = '', explanation = '') => {
  chosenValue.value = value;
  chosenSource.value = source;
  chosenConfidence.value = confidence;
  chosenExplanation.value = explanation;
};

watch(selectedOption, newValue => {
  if (newValue && Object.keys(newValue).length > 0) {
    const { value, source, confidence, explanation } = newValue;
    setChosen(value, source, confidence, explanation);
    return;
  }

  // Revert to the inputValue if there's one
  if (inputValue.value) {
    setChosen(inputValue.value, 'Manual entry', 'Unknown', 'This information was manually entered by you.');
  } else {
    setChosen();
  }
}, {
  deep: true,
});

watch(inputValue, newValue => {
  if (newValue) {
    setChosen(newValue, 'Manual entry', 'Unknown', 'This information was manually entered by you.');
    return;
  }

  // Revert to the selectedOption value if there's one
  if (selectedOption.value && Object.keys(selectedOption.value).length > 0) {
    const { value, source, confidence, explanation } = selectedOption.value;
    setChosen(value, source, confidence, explanation);
  } else {
    setChosen();
  }
});

watch(chosenValue, () => {
  emit('update', chosenValue.value, chosenSource.value, chosenConfidence.value, chosenExplanation.value);
});
</script>

<style scoped>
.field-source-info {
  color: var(--stk-text-weak);
  font-size: 0.75rem;
}
</style>
